import React, { useState, useMemo, useEffect } from 'react'
import type { BuilderAngle } from '../types'
import { useBetBuilder } from '../context/BetContext'
import { useOdds } from '../context/OddsContext'
import { angleMarketToOddsMarket } from '../utils'
import { calculateValueDisplay } from '../utils/odds'
import { EdgeBadge } from './EdgeBadge'
import { AvailabilityBadge } from './AvailabilityBadge'
import { FreshnessIndicator } from './FreshnessIndicator'
import type { AvailabilityStatus } from '../types'

interface PlayerMarketCardProps {
  marketGroup: string // e.g., 'Cards', 'Goals', 'Shots'
  angles: BuilderAngle[] // All player angles for this market type
  fixtureId: number
  fixtureLabel: string
  alwaysExpanded?: boolean // When true, show content without header (for use inside accordion)
}

function getConfidenceStyle(confidence: string): React.CSSProperties {
  switch (confidence) {
    case 'high':
      return {
        background: 'rgba(74, 222, 128, 0.15)',
        borderColor: 'rgba(74, 222, 128, 0.4)',
        color: 'var(--green)',
      }
    case 'med':
      return {
        background: 'rgba(251, 191, 36, 0.15)',
        borderColor: 'rgba(251, 191, 36, 0.4)',
        color: 'var(--yellow)',
      }
    case 'low':
    default:
      return {
        background: 'rgba(251, 113, 133, 0.15)',
        borderColor: 'rgba(251, 113, 133, 0.4)',
        color: 'var(--red)',
      }
  }
}

function getMarketType(group: string): 'corners' | 'shots' | 'shots_on_target' | 'cards' | 'goals' | 'other' {
  const g = group.toLowerCase()
  if (g.includes('card')) return 'cards'
  if (g.includes('goal')) return 'goals'
  // Check for "shots on target" BEFORE general "shots" (more specific first)
  if (g.includes('shots on target') || g === 'sot') return 'shots_on_target'
  if (g.includes('shot')) return 'shots'
  return 'other'
}

function getMarketIcon(group: string): string {
  const g = group.toLowerCase()
  if (g.includes('card')) return '🟨'
  if (g.includes('score or assist') || g.includes('score-or-assist')) return '⚽'
  if (g.includes('goal')) return '⚽'
  if (g.includes('shots on target') || g === 'sot') return '🎯'
  if (g.includes('shot')) return '🎯'
  if (g.includes('assist')) return '👟'
  if (g.includes('foul')) return '⚠️'
  if (g.includes('save')) return '🧤'
  return '📊'
}

function getMarketTitle(group: string): string {
  const g = group.toLowerCase()
  if (g.includes('card')) return 'Player to be Booked'
  // Check score-or-assist BEFORE goal (more specific first)
  if (g.includes('score or assist') || g.includes('score-or-assist')) return 'To Score or Assist'
  if (g.includes('goal') && g.includes('2+')) return 'Player to Score 2+ Goals'
  if (g.includes('goal')) return 'Anytime Goalscorer'
  if (g.includes('shots on target') || g === 'sot') return 'Player Shots on Target'
  if (g.includes('shot')) return 'Player Shots'
  if (g.includes('assist')) return 'Player Assists'
  if (g.includes('foul')) return 'Player Fouls'
  if (g.includes('save')) return 'Goalkeeper Saves'
  return `Player ${group}`
}

function getConfidenceFromPct(pct: number) {
  if (pct >= 65) return 'high'
  if (pct >= 40) return 'med'
  return 'low'
}

// Check if market is binary (no lines needed)
function isBinaryMarket(group: string): boolean {
  const g = group.toLowerCase()
  // Cards = booked yes/no, Goals = anytime scorer, Assists = anytime assist, Score or Assist = goal or assist
  return g.includes('card') || g.includes('goal') || g.includes('assist') || g.includes('score or assist') || g.includes('score-or-assist')
}

// Available lines for non-binary markets
const LINE_OPTIONS = [0.5, 1.5, 2.5, 3.5, 4.5]

// Individual player row component
function PlayerRow({
  angle,
  fixtureId,
  fixtureLabel,
  marketGroup,
  selectedLine,
  isOver,
  oddsMarketSlug,
}: {
  angle: BuilderAngle
  fixtureId: number
  fixtureLabel: string
  marketGroup: string
  selectedLine: number
  isOver: boolean
  oddsMarketSlug: string | null
}) {
  const [showTooltip, setShowTooltip] = useState(false)
  const { addBet, removeBet, isBetSaved } = useBetBuilder()
  const {
    getBestOddsForPlayer,
    getOddsForPlayerBookmaker,
    preferredBookmaker,
    formatOdds,
    oddsHidden,
    showValueBadges,
    minEdgeThreshold,
  } = useOdds()

  const isBinary = isBinaryMarket(marketGroup)

  // Phase 6.5: Check player availability status
  const availabilityStatus = angle.entity?.availability_status as AvailabilityStatus | undefined
  const availabilityReason = angle.entity?.availability_reason as string | undefined
  const isUnavailable = availabilityStatus === 'injured' || availabilityStatus === 'suspended' || availabilityStatus === 'confirmed_out'

  // Get odds for this player (binary markets like anytime goalscorer)
  const playerName = angle.entity?.player_name ?? ''
  const bestOdds = isBinary && oddsMarketSlug
    ? getBestOddsForPlayer(fixtureId, oddsMarketSlug, playerName)
    : null
  const preferredOdds = isBinary && oddsMarketSlug && preferredBookmaker
    ? getOddsForPlayerBookmaker(fixtureId, oddsMarketSlug, playerName, preferredBookmaker.slug)
    : null

  // Calculate conviction for the selected line
  const basePct = angle.conviction_pct ?? 0
  const rawPct = isBinary
    ? basePct
    : (angle.line_convictions?.[String(selectedLine)]?.raw_pct ?? basePct)
  const adjustedPct = isBinary
    ? basePct
    : (angle.line_convictions?.[String(selectedLine)]?.adjusted_pct ?? basePct)
  const linePct = isBinary
    ? basePct
    : isOver
      ? adjustedPct
      : 100 - adjustedPct

  const confidence = getConfidenceFromPct(linePct)
  const confidenceStyle = getConfidenceStyle(confidence)

  // Build bet ID based on market type
  const betId = isBinary
    ? `${angle.market_id}-${angle.entity?.player_name}`
    : `${angle.market_id}-${angle.entity?.player_name}-${isOver ? 'over' : 'under'}-${selectedLine}`
  const isAdded = isBetSaved(betId)

  const handleAddBet = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isAdded) {
      removeBet(betId)
    } else {
      const betTitle = isBinary
        ? `${angle.entity?.player_name} - ${getMarketTitle(marketGroup)}`
        : `${angle.entity?.player_name} ${marketGroup} ${isOver ? 'Over' : 'Under'} ${selectedLine}`
      addBet({
        id: betId,
        fixtureId,
        fixture: fixtureLabel,
        title: betTitle,
        line: isBinary ? undefined : selectedLine,
        marketType: getMarketType(marketGroup),
        scope: 'player',
        teamType: 'total',
        isOver,
        confidence,
        confidencePct: linePct,
      })
    }
  }

  // Calculate edge for value betting display
  const preferredEdge = showValueBadges && preferredOdds != null
    ? calculateValueDisplay(linePct, preferredOdds, minEdgeThreshold)
    : null
  const bestEdge = showValueBadges && bestOdds?.odds != null
    ? calculateValueDisplay(linePct, bestOdds.odds, minEdgeThreshold)
    : null

  return (
    <div className="player-row" style={{ opacity: isUnavailable ? 0.5 : 1 }}>
      <div className="player-row-main">
        <div className="player-row-info">
          <div className="player-row-name">
            {angle.entity?.player_name}
            <span className="player-row-position">{angle.entity?.position}</span>
            {availabilityStatus && availabilityStatus !== 'available' && (
              <AvailabilityBadge status={availabilityStatus} reason={availabilityReason} compact />
            )}
          </div>
          <div className="player-row-why">{angle.why}</div>
        </div>
        <div className="player-row-right">
          <div style={{ position: 'relative' }}>
            <span
              className="pill"
              style={{
                ...confidenceStyle,
                border: `1px solid ${confidenceStyle.borderColor}`,
                fontWeight: 700,
                fontSize: 12,
                padding: '3px 8px',
                cursor: 'help',
              }}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              {Math.round(linePct)}%
            </span>
            {showTooltip && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: 8,
                  padding: '10px 12px',
                  background: '#1a2340',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  zIndex: 100,
                  minWidth: 180,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                }}
              >
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
                  Probability Breakdown
                </div>
                {!isBinary && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>Raw:</span>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{Math.round(isOver ? rawPct : 100 - rawPct)}%</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>vs Opponent:</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: confidenceStyle.color }}>{Math.round(linePct)}%</span>
                    </div>
                  </>
                )}
                {isBinary && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>Probability:</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: confidenceStyle.color }}>{Math.round(basePct)}%</span>
                  </div>
                )}
                <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 8, lineHeight: 1.4 }}>
                  {isBinary ? (
                    'Based on player\'s recent form and opponent analysis'
                  ) : (
                    <>Raw = pure statistical probability<br />Adjusted = accounts for opponent strength</>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Odds display for binary player props */}
          {!oddsHidden && isBinary && (
            <div className="player-odds-display">
              <span className="player-odds-value preferred">
                {preferredOdds != null ? formatOdds(preferredOdds) : '—'}
                {preferredEdge && (
                  <EdgeBadge
                    value={preferredEdge}
                    ourProbability={linePct}
                    bookmakerName={preferredBookmaker?.name}
                  />
                )}
              </span>
              <span className="player-odds-value best">
                {bestOdds?.odds != null ? formatOdds(bestOdds.odds) : '—'}
                {bestEdge && (
                  <EdgeBadge
                    value={bestEdge}
                    ourProbability={linePct}
                    bookmakerName={bestOdds?.bookmaker_slug ? `Best: ${bestOdds.bookmaker_slug}` : 'Best odds'}
                  />
                )}
              </span>
            </div>
          )}
          <button
            onClick={handleAddBet}
            className={`add-btn ${isAdded ? 'added' : ''}`}
          >
            {isAdded ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export function PlayerMarketCard({ marketGroup, angles, fixtureId, fixtureLabel, alwaysExpanded = false }: PlayerMarketCardProps) {
  const [isExpanded, setIsExpanded] = useState(alwaysExpanded)
  const [selectedLine, setSelectedLine] = useState(0.5)
  const [isOver, setIsOver] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const { fetchFixtureOddsData, forceRefreshOdds, getFixtureOddsData, oddsHidden } = useOdds()

  const isBinary = isBinaryMarket(marketGroup)

  // Get odds data for freshness indicator
  const oddsData = getFixtureOddsData(fixtureId)

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await forceRefreshOdds(fixtureId)
    } finally {
      setIsRefreshing(false)
    }
  }

  // Map market group to odds market slug
  const oddsMarketSlug = useMemo(() => {
    // Get market_type from first angle if available
    const firstAngle = angles[0]
    if (firstAngle?.market_type) {
      return angleMarketToOddsMarket(firstAngle.market_type, 'total')
    }
    return null
  }, [angles])

  // Fetch odds when component mounts
  useEffect(() => {
    if (fixtureId) {
      fetchFixtureOddsData(fixtureId)
    }
  }, [fixtureId, fetchFixtureOddsData])

  // Deduplicate players by player_id (keep the one with highest base conviction)
  const uniqueAngles = useMemo(() => {
    const playerMap = new Map<number, BuilderAngle>()
    for (const angle of angles) {
      const playerId = angle.entity?.player_id
      if (playerId == null) continue
      const existing = playerMap.get(playerId)
      if (!existing || (angle.conviction_pct ?? 0) > (existing.conviction_pct ?? 0)) {
        playerMap.set(playerId, angle)
      }
    }
    return Array.from(playerMap.values())
  }, [angles])

  // Sort players by conviction percentage for the selected line (highest first)
  const sortedAngles = useMemo(() => {
    return [...uniqueAngles].sort((a, b) => {
      if (isBinary) {
        return (b.conviction_pct ?? 0) - (a.conviction_pct ?? 0)
      }
      // For non-binary, sort by the selected line's conviction
      const aPct = isOver
        ? (a.line_convictions?.[String(selectedLine)]?.adjusted_pct ?? a.conviction_pct ?? 0)
        : 100 - (a.line_convictions?.[String(selectedLine)]?.adjusted_pct ?? a.conviction_pct ?? 0)
      const bPct = isOver
        ? (b.line_convictions?.[String(selectedLine)]?.adjusted_pct ?? b.conviction_pct ?? 0)
        : 100 - (b.line_convictions?.[String(selectedLine)]?.adjusted_pct ?? b.conviction_pct ?? 0)
      return bPct - aPct
    })
  }, [uniqueAngles, selectedLine, isOver, isBinary])

  // Top 3 players preview (when collapsed)
  const topPlayers = sortedAngles.slice(0, 3)

  // When alwaysExpanded, render just the content without the card wrapper
  if (alwaysExpanded) {
    return (
      <div className="player-market-content">
        {/* Filters for non-binary markets */}
        {!isBinary && (
          <div className="player-market-filters" style={{ marginBottom: 12 }}>
            {/* Over/Under toggle */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
              <button
                className={`pill pill-clickable ${isOver ? 'pill-active' : ''}`}
                onClick={(e) => { e.stopPropagation(); setIsOver(true) }}
                style={{ fontSize: 11, padding: '4px 10px' }}
              >
                Over
              </button>
              <button
                className={`pill pill-clickable ${!isOver ? 'pill-active' : ''}`}
                onClick={(e) => { e.stopPropagation(); setIsOver(false) }}
                style={{ fontSize: 11, padding: '4px 10px' }}
              >
                Under
              </button>
            </div>
            {/* Line options */}
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {LINE_OPTIONS.map((line) => (
                <button
                  key={line}
                  className={`pill pill-clickable ${selectedLine === line ? 'pill-active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setSelectedLine(line) }}
                  style={{ fontSize: 11, padding: '4px 10px' }}
                >
                  {line}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="player-list">
          {sortedAngles.map((angle) => (
            <PlayerRow
              key={angle.entity?.player_id}
              angle={angle}
              fixtureId={fixtureId}
              fixtureLabel={fixtureLabel}
              marketGroup={marketGroup}
              selectedLine={selectedLine}
              isOver={isOver}
              oddsMarketSlug={oddsMarketSlug}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="card" style={{ background: 'rgba(255,255,255,.03)' }}>
      <div className="card-inner player-market">
        {/* Header - always visible */}
        <div
          className="player-market-header"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: 'pointer' }}
        >
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18 }}>{getMarketIcon(marketGroup)}</span>
              <div>
                <div className="angle-title">{getMarketTitle(marketGroup)}</div>
                <div className="small" style={{ color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{uniqueAngles.length} players</span>
                  {!oddsHidden && oddsData && (
                    <FreshnessIndicator
                      updatedAt={oddsData.updated_at}
                      onRefresh={handleRefresh}
                      showRefreshButton
                      isRefreshing={isRefreshing}
                    />
                  )}
                </div>
              </div>
            </div>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{
                transition: 'transform 0.2s',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>

          {/* Quick preview when collapsed */}
          {!isExpanded && (
            <div className="player-preview" style={{ marginTop: 10 }}>
              {topPlayers.map((angle, idx) => {
                const basePct = angle.conviction_pct ?? 0
                const pct = isBinary
                  ? basePct
                  : isOver
                    ? (angle.line_convictions?.[String(selectedLine)]?.adjusted_pct ?? basePct)
                    : 100 - (angle.line_convictions?.[String(selectedLine)]?.adjusted_pct ?? basePct)
                const conf = getConfidenceFromPct(pct)
                const style = getConfidenceStyle(conf)
                return (
                  <div
                    key={idx}
                    className="player-preview-item"
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '4px 0',
                      borderBottom: idx < topPlayers.length - 1 ? '1px solid var(--border)' : 'none',
                    }}
                  >
                    <span style={{ fontSize: 13 }}>{angle.entity?.player_name}</span>
                    <span
                      className="pill"
                      style={{
                        ...style,
                        border: `1px solid ${style.borderColor}`,
                        fontSize: 11,
                        padding: '2px 6px',
                      }}
                    >
                      {Math.round(pct)}%
                    </span>
                  </div>
                )
              })}
              {uniqueAngles.length > 3 && (
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4, textAlign: 'center' }}>
                  +{uniqueAngles.length - 3} more players
                </div>
              )}
            </div>
          )}
        </div>

        {/* Expanded content - all players list */}
        {isExpanded && (
          <div className="player-market-content" style={{ marginTop: 12 }}>
            {/* Filters for non-binary markets */}
            {!isBinary && (
              <div className="player-market-filters" style={{ marginBottom: 12 }}>
                {/* Over/Under toggle */}
                <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                  <button
                    className={`pill pill-clickable ${isOver ? 'pill-active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setIsOver(true) }}
                    style={{ fontSize: 11, padding: '4px 10px' }}
                  >
                    Over
                  </button>
                  <button
                    className={`pill pill-clickable ${!isOver ? 'pill-active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setIsOver(false) }}
                    style={{ fontSize: 11, padding: '4px 10px' }}
                  >
                    Under
                  </button>
                </div>
                {/* Line options */}
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {LINE_OPTIONS.map((line) => (
                    <button
                      key={line}
                      className={`pill pill-clickable ${selectedLine === line ? 'pill-active' : ''}`}
                      onClick={(e) => { e.stopPropagation(); setSelectedLine(line) }}
                      style={{ fontSize: 11, padding: '4px 10px' }}
                    >
                      {line}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="player-list">
              {sortedAngles.map((angle) => (
                <PlayerRow
                  key={angle.entity?.player_id}
                  angle={angle}
                  fixtureId={fixtureId}
                  fixtureLabel={fixtureLabel}
                  marketGroup={marketGroup}
                  selectedLine={selectedLine}
                  isOver={isOver}
                  oddsMarketSlug={oddsMarketSlug}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
